import React, { useEffect, useState } from 'react';
import { Box, styled } from '@mui/material';
import ArrowForwardIosIcon from '@mui/icons-material/ArrowForwardIos';
import NavigationButton from '../shared/Buttons/NavigationButton';
import ArrowBackIosNewIcon from '@mui/icons-material/ArrowBackIosNew';

const PaginationWrapper = styled(Box)(() => ({
    display: 'flex',
    alignItem: 'center',
    justifyContent: 'center',
}));

const PaginationItemWrapper = styled(Box)(() => ({
    marginRight: '1.5rem',
}));

const PaginationItem = styled(Box)(() => ({
    border: '1px solid transparent',
    borderRadius: '50%',
    display: 'inline-flex',
    justifyContent: 'center',
    alignItems: 'center',
    position: 'relative',
    width: '40px',
    height: '40px',
    padding: ' 5px',
    cursor: 'pointer',

    '&.selected': {
        border: '1px solid black',
    },
}));

export const CollectionPagination: React.FC<PaginationProps> = (props) => {
    const { count, productsPerPage, handlePagination, pagination } = props;

    const [pageNumber, setPageNumber] = useState(
        Math.floor(count / productsPerPage)
    );
    const [currentPage, setCurrentPage] = useState(pagination);

    const selectPageNumber = (number: number) => {
        handlePagination(number);
        setCurrentPage(number);
    };

    const addRow = (number: number) => {
        if (number != 0) {
            return (
                <PaginationItemWrapper key={number}>
                    <PaginationItem
                        className={number == currentPage ? 'selected' : ''}
                        onClick={() => selectPageNumber(number)}
                    >
                        {number}
                    </PaginationItem>
                </PaginationItemWrapper>
            );
        } else {
            return (
                <PaginationItemWrapper key={number}>
                    <PaginationItem>...</PaginationItem>
                </PaginationItemWrapper>
            );
        }
    };

    const setPaginationListItems = () => {
        const rows = [];

        if (pageNumber <= 3) {
            for (let i = 1; i <= pageNumber; i++) {
                rows.push(addRow(i));
            }
        } else {
            rows.push(addRow(1));

            if (currentPage > 3) {
                rows.push(addRow(0));
            }

            if (currentPage == pageNumber) {
                rows.push(addRow(currentPage - 2));
            }

            if (currentPage > 2) {
                rows.push(addRow(currentPage - 1));
            }

            if (currentPage != 1 && currentPage != pageNumber) {
                rows.push(addRow(currentPage));
            }

            if (currentPage < pageNumber - 1) {
                rows.push(addRow(currentPage + 1));
            }

            if (currentPage == 1) {
                rows.push(addRow(currentPage + 2));
            }

            if (currentPage < pageNumber - 2) {
                rows.push(addRow(0));
            }

            rows.push(addRow(pageNumber));
        }

        return <>{rows}</>;
    };

    useEffect(() => {
        setPaginationListItems();
    }, [currentPage]);

    useEffect(() => {
        setCurrentPage(pagination);
    }, [pagination]);

    useEffect(() => {
        if (count) {
            setPageNumber(Math.ceil(count / productsPerPage));
        }
    }, [count]);

    const nextPage = () => {
        if (pageNumber != pagination) {
            handlePagination(pagination + 1);
            setCurrentPage(pagination + 1);
        }
    };

    const previousPage = () => {
        if (pagination != 0) {
            handlePagination(pagination - 1);
            setCurrentPage(pagination - 1);
        }
    };

    return (
        <PaginationWrapper>
            {pageNumber != 1 && (
                <>
                    <NavigationButton
                        onClick={previousPage}
                        disabled={currentPage == 1}
                        sx={{ marginRight: '1.5rem', color: '#2B3445' }}
                    >
                        <ArrowBackIosNewIcon />
                    </NavigationButton>
                    {setPaginationListItems()}
                    <NavigationButton
                        onClick={nextPage}
                        disabled={currentPage == pageNumber}
                        sx={{ marginRight: '1.5rem', color: '#2B3445' }}
                    >
                        <ArrowForwardIosIcon />
                    </NavigationButton>
                </>
            )}
        </PaginationWrapper>
    );
};

type PaginationProps = {
    count: number;
    productsPerPage: number;
    handlePagination: (pagination: number) => void;
    pagination: number;
};
